import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { AlertColor } from '@mui/material'
import { getDataFromLocalStorage, saveToLocalStorage } from '@/utils/localStorage'

type ThemeMode = 'light' | 'dark'

const THEME_MODE_KEY = 'themeMode'

/**
 * Reads the persisted theme from localStorage. Wrapped in try/catch because
 * storage can throw (Safari private mode, blocked site data, iOS WebViews
 * with storage disabled) — in that case we fall back to the default.
 */
const loadThemeMode = (): ThemeMode => {
  try {
    const stored = getDataFromLocalStorage(THEME_MODE_KEY)
    return stored === 'dark' || stored === 'light' ? stored : 'light'
  } catch {
    return 'light'
  }
}

const persistThemeMode = (mode: ThemeMode) => {
  try {
    saveToLocalStorage(THEME_MODE_KEY, mode)
  } catch {
    // Storage unavailable — theme still applies for the current session.
  }
}

/**
 * UI slice — stores global UI state.
 * Sidebar, theme, global loader, snackbar toast, notification queue.
 */
interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  message: string
}

interface UIState {
  // Sidebar
  sidebarOpen: boolean
  sidebarCollapsed: boolean
  sidebarMobileOpen: boolean
  // Theme
  themeMode: ThemeMode
  // Global loader (full-screen spinner overlay)
  showLoader: boolean
  // Snackbar toast (single message at a time)
  snackbar: {
    open: boolean
    severity: AlertColor
    message: string
  }
  // Notification queue (stacked toasts)
  notifications: Notification[]
}

const initialState: UIState = {
  sidebarOpen: true,
  sidebarCollapsed: false,
  sidebarMobileOpen: false,
  themeMode: loadThemeMode(),
  showLoader: false,
  snackbar: {
    open: false,
    severity: 'success',
    message: '',
  },
  notifications: [],
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // ── Sidebar ────────────────────────────────────────────────────────────────
    toggleSidebar(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed
    },
    setSidebarOpen(state, action: PayloadAction<boolean>) {
      state.sidebarOpen = action.payload
    },
    setSidebarMobileOpen(state, action: PayloadAction<boolean>) {
      state.sidebarMobileOpen = action.payload
    },

    // ── Theme ──────────────────────────────────────────────────────────────────
    toggleTheme(state) {
      state.themeMode = state.themeMode === 'light' ? 'dark' : 'light'
      persistThemeMode(state.themeMode)
    },
    setThemeMode(state, action: PayloadAction<ThemeMode>) {
      state.themeMode = action.payload
      persistThemeMode(state.themeMode)
    },

    // ── Global loader ──────────────────────────────────────────────────────────
    showLoader(state) {
      state.showLoader = true
    },
    hideLoader(state) {
      state.showLoader = false
    },

    // ── Snackbar toast ─────────────────────────────────────────────────────────
    showSnackbar(state, action: PayloadAction<{ severity: AlertColor; message: string }>) {
      state.snackbar.open = true
      state.snackbar.severity = action.payload.severity
      state.snackbar.message = action.payload.message
    },
    hideSnackbar(state) {
      state.snackbar.open = false
    },

    // ── Notification queue ─────────────────────────────────────────────────────
    addNotification(state, action: PayloadAction<Omit<Notification, 'id'>>) {
      const id = Date.now().toString()
      state.notifications.push({ id, ...action.payload })
    },
    removeNotification(state, action: PayloadAction<string>) {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload)
    },
  },
})

export const {
  toggleSidebar,
  setSidebarOpen,
  setSidebarMobileOpen,
  toggleTheme,
  setThemeMode,
  showLoader,
  hideLoader,
  showSnackbar,
  hideSnackbar,
  addNotification,
  removeNotification,
} = uiSlice.actions
export const uiReducer = uiSlice.reducer
